// Import types but defer loading the actual SDK
import type {
  SingularConfig,
  singularSdk as SingularSdkType,
} from "singular-sdk";

import { isInternalEmail } from "@medbillai/utils/email";

import type {
  SingularConfigProps,
  SingularEventArgs,
  SingularEventName,
} from "./types";

// Check if we're in a browser environment
const isBrowser = typeof window !== "undefined";

// Load SDK only in browser
let singularSdk: typeof SingularSdkType | undefined;
let SingularConfigClass: typeof SingularConfig | undefined;
let sdkLoadPromise: Promise<void> | undefined;

// Type-safe module loading
if (isBrowser) {
  sdkLoadPromise = (async () => {
    try {
      const singularModule = await import("singular-sdk");

      if (
        singularModule &&
        "singularSdk" in singularModule &&
        "SingularConfig" in singularModule
      ) {
        singularSdk = singularModule.singularSdk;
        SingularConfigClass = singularModule.SingularConfig;
      } else {
        console.error(
          "[SINGULAR]: Invalid module format - missing required singular-sdk components",
        );
      }
    } catch (error) {
      console.error("[SINGULAR]: Failed to load SDK:", error);
    }
  })();
}

class SingularService {
  private initialized = false;
  private userId: string | undefined = undefined;
  private userEmail: string | undefined = undefined;
  private singularTrackingEnabled: boolean = false;

  async initialize(config: SingularConfigProps): Promise<void> {
    // Early return if not in browser or tracking disabled
    if (!isBrowser || config.singularTrackingEnabled === false) {
      console.log(
        "[SINGULAR]: Skipping initialization (not in browser or tracking disabled)",
      );
      this.singularTrackingEnabled = false;
      return;
    }

    if (this.initialized) {
      console.warn("[SINGULAR]: Already initialized");
      return;
    }

    // Wait for SDK to load if it hasn't already
    if (sdkLoadPromise) {
      console.log("[SINGULAR]: Waiting for SDK to load...");
      try {
        await sdkLoadPromise;
      } catch (error) {
        console.error(
          "[SINGULAR]: Error while waiting for SDK to load:",
          error,
        );
        return;
      }
    }

    // Check if SDK loaded properly
    if (!singularSdk || !SingularConfigClass) {
      console.error(
        "[SINGULAR]: SDK not loaded properly - missing SDK components",
      );
      return;
    }

    const singularConfig = new SingularConfigClass(
      config.singularSdkKey,
      config.singularSdkSecret,
      "com.granted.app",
    );
    // Set session timeout equivalent to the native 180 seconds
    singularConfig.withSessionTimeoutInMinutes(3);
    singularConfig.withAutoPersistentSingularDeviceId("grantedhealth.com");

    if (this.userId !== undefined) {
      singularConfig.withCustomUserId(this.userId);
    }

    singularSdk.init(singularConfig);
    this.initialized = true;
    this.singularTrackingEnabled = true;
  }

  setUser({ id, email }: { id: string; email?: string }) {
    if (!isBrowser || !singularSdk) {
      return;
    }

    if (!this.initialized && this.singularTrackingEnabled) {
      console.warn(
        "[SINGULAR]: Not initialized before setting user, storing user data on service till initialization",
      );
    }

    this.userId = id;

    if (email) {
      this.userEmail = email;
    }

    singularSdk.login(id);
  }

  resetUser() {
    if (!isBrowser || !singularSdk) {
      return;
    }

    if (!this.initialized && this.singularTrackingEnabled) {
      console.warn("[SINGULAR]: Not initialized before resetting user");
    }
    this.userId = undefined;
    this.userEmail = undefined;
    if (this.singularTrackingEnabled) {
      singularSdk.logout();
    }
  }

  event(eventName: SingularEventName, args?: SingularEventArgs) {
    if (!isBrowser || !singularSdk) {
      return;
    }

    if (!this.initialized && this.singularTrackingEnabled) {
      console.warn("[SINGULAR]: Not initialized before sending events");
    }

    if (!this.singularTrackingEnabled) {
      return;
    }

    // Check if user has internal email domain - skip event if so
    if (isInternalEmail(this.userEmail)) {
      console.log("[SINGULAR]: Skipping event for internal user");
      return;
    }

    if (args) {
      singularSdk.event(eventName, args);
    } else {
      singularSdk.event(eventName);
    }
  }
}

const singularService = new SingularService();
export { singularService };
